import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getStatus } from "../services/flightBookingAPI";
import { auth } from "../firebase";
import BookingConfirmation from "../components/bookingFlight/BookingConfirmation";
import "../../src/components/componetsChat/style/skeleton.css";

const BookingDetail = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [bookingData, setBookingData] = useState(null);
    const [error, setError] = useState(null);

    const rcBookingId = searchParams.get("rc_booking_id");
    const traceId = searchParams.get("trace_id");
    const resultIdx = searchParams.get("result_idx");
    const amount = searchParams.get("amount");

    useEffect(() => {
        const fetchBookingDetail = async (userContact, userToken) => {
            if (!rcBookingId) {
                navigate("/booking-history");
                return;
            }

            if (!userContact) {
                setError("Please login to view booking details");
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const response = await getStatus(
                    rcBookingId,
                    traceId,
                    resultIdx,
                    amount,
                    userToken, // Use actual user token
                    userContact
                );

                const booking = response?.data?.bookings?.[0] || response?.bookings?.[0] || response?.data?.[0];

                if (booking) {
                    // Helper to parse double-stringified JSON
                    const robustParse = (data) => {
                        if (!data) return null;
                        if (typeof data === "object") return data;
                        try {
                            const first = JSON.parse(data);
                            if (typeof first === "string") return JSON.parse(first);
                            return first;
                        } catch { return data; }
                    };

                    let processedData = {
                        ...booking,
                        ticket_details: robustParse(booking.ticket_details),
                        booking_request: robustParse(booking.booking_request)
                    };

                    setBookingData(processedData);
                } else {
                    setError("Booking not found");
                }
            } catch (err) {
                console.error("[BookingDetail] Error:", err);
                setError(err.message || "Failed to load booking details");
            } finally {
                setIsLoading(false);
            }
        };

        // Use Firebase listener to handle auth state initialization
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (user) {
                try {
                    const contact = user.email || user.phoneNumber?.replace("+91", "");
                    const token = await user.getIdToken();
                    fetchBookingDetail(contact, token);
                } catch (err) {
                    console.error("[BookingDetail] Auth error:", err);
                    setError("Failed to authenticate user");
                    setIsLoading(false);
                }
            } else {
                // Check local storage fallback for custom auth tokens if Firebase is not used/available
                const storedUser = localStorage.getItem("user");
                if (storedUser) {
                    try {
                        const parsedUser = JSON.parse(storedUser);
                        if (parsedUser.token) {
                            // Derive contact from stored user if possible, or use a default
                            const contact = parsedUser.email || parsedUser.phone || "";
                            if (contact) {
                                fetchBookingDetail(contact, parsedUser.token);
                                return;
                            }
                        }
                    } catch (e) {
                        console.error("[BookingDetail] Local storage parse error:", e);
                    }
                }

                setError("Please login to view booking details");
                setIsLoading(false);
            }
        });

        return () => unsubscribe();
    }, [rcBookingId, navigate]);

    return (
        <div className="bg-gray-50 min-h-screen pt-20 pb-12">
            <main className="mx-auto max-w-[1400px] pt-8 px-4">
                <button
                    onClick={() => navigate("/booking-history")}
                    className="mb-4 flex items-center gap-2 px-4 py-2 text-[#475569] hover:text-[#0F172A] font-medium transition-all group"
                >
                    <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to My Bookings
                </button>

                {isLoading ? (
                    <div className="mt-8 px-6 space-y-10 max-w-[1200px] mx-auto pb-20">
                        {/* 1st Section: Top Large Block */}
                        <div className="flex justify-center">
                            <div className="w-[45%] h-[110px] shimmer rounded-[4px]" />
                        </div>

                        {/* 2nd Section: Wide Banner with Center Bars & Semi-circle */}
                        <div className="w-full h-[160px] shimmer rounded-[4px] relative bg-[#f1f4f7] flex items-center justify-center">
                            <div className="flex flex-col items-center gap-2.5">
                                <div className="w-[180px] h-[32px] shimmer bg-[#e2e8f0] rounded-[2px]" />
                                <div className="w-[180px] h-[32px] shimmer bg-[#e2e8f0] rounded-[2px]" />
                                <div className="w-[180px] h-[32px] shimmer bg-[#e2e8f0] rounded-[2px]" />
                            </div>
                            {/* Semi-circle on right */}
                            <div className="absolute right-[-40px] w-64 h-64 bg-[#e2e8f0] rounded-full opacity-30 translate-x-12" />
                        </div>

                        {/* 3rd Section: Two Column Content */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                            {/* Left Column Card */}
                            <div className="w-full bg-[#f8fafc] rounded-[8px] p-6 space-y-4 h-[420px]">
                                <div className="w-full h-[28px] shimmer rounded-[4px]" />
                                <div className="space-y-3">
                                    <div className="w-full h-[32px] shimmer rounded-[4px]" />
                                    <div className="w-full h-[220px] shimmer rounded-[4px]" />
                                </div>
                            </div>

                            {/* Right Column Bars */}
                            <div className="space-y-8">
                                <div className="space-y-4">
                                    <div className="w-full h-[28px] shimmer rounded-[4px]" />
                                    <div className="w-[90%] h-[32px] shimmer rounded-[4px] mx-auto" />
                                </div>
                                <div className="w-full border-t border-gray-100 pt-8 space-y-4">
                                    <div className="w-full h-[28px] shimmer rounded-[4px]" />
                                    <div className="w-full h-[100px] shimmer rounded-[4px]" />
                                    <div className="w-full h-[28px] shimmer rounded-[4px]" />
                                </div>
                            </div>
                        </div>

                        {/* 4th Section: Options Row */}
                        <div className="space-y-6 pt-10">
                            <div className="w-full h-[24px] shimmer rounded-[4px]" />
                            <div className="flex justify-between gap-4">
                                {[1, 2, 3, 4, 5].map(i => (
                                    <div key={i} className="flex-1 h-[32px] shimmer rounded-[4px]" />
                                ))}
                            </div>
                        </div>

                        {/* 5th Section: Bottom Blocks Row */}
                        <div className="space-y-6 pt-6">
                            <div className="w-full h-[24px] shimmer rounded-[4px]" />
                            <div className="flex justify-between gap-6">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="flex-1 h-[100px] shimmer rounded-[4px]" />
                                ))}
                            </div>
                        </div>
                    </div>
                ) : error ? (
                    <div className="mt-32 max-w-2xl mx-auto bg-red-50 border border-red-200 text-red-700 px-6 py-8 rounded-2xl text-center shadow-sm">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold mb-2 text-red-800">Booking Insight</h2>
                        <p className="text-red-600/80 mb-6">{error}</p>
                        <button
                            onClick={() => navigate("/booking-history")}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-[#1A2233] text-white rounded-xl font-medium hover:bg-[#2a354a] transition-all shadow-md active:scale-95"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5M12 19l-7-7 7-7" />
                            </svg>
                            Back to My Bookings
                        </button>
                    </div>
                ) : bookingData ? (
                    <BookingConfirmation
                        onContactSupport={() => navigate('/chat')}
                        trace_id={bookingData?.trace_id}
                        result_idx={bookingData?.result_idx}
                        flightDetails={
                            bookingData?.ticket_details?.FlightItinerary ||
                            bookingData?.Response?.Response?.FlightItinerary ||
                            bookingData?.FlightItinerary ||
                            bookingData?.booking_request?.FlightItinerary ||
                            bookingData?.booking_request?.Response?.Response?.FlightItinerary || bookingData?.ticket_details?.Response?.FlightItinerary ||
                            null
                        }
                        bookingData={bookingData}
                    />
                ) : null}
            </main>

        </div>
    );
};

export default BookingDetail;
